import React, { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import * as _ from 'lodash'
import * as actions from '../actions';
import valueFilters from '../valueFilters';
import globalRelay from '../globalRelay';
import util from '../util';
import keyboardUtil from 'Scripts/keyboardUtils';
import { CurrencyInput, Expander, jobCostRowOwner, JobCostHiddenWhenLocked, NumericInput, Radio, TooltipTarget } from './sharedComponents';
import { IJobCostChildProps } from './jobCostEdit';
import { IJobCostLineItem } from 'Scripts/models/jobCostLineItemModel';
import { IInstallSheet } from 'Scripts/models/installSheetModel';

const KEY_DOWN_CODES = [13];
type jobCostUpdater = Pick<IJobCostChildProps, 'updateModel'>;

function CustomTableRow({ 
	alwaysValidate = false, 
	buttonClass, buttonHandler, 
	classNames = [], 
	companyVariant, 
	lineItem, 
	updateModel }: 
    { 
        alwaysValidate?: boolean, 
        buttonClass: string, 
        buttonHandler: (isValid: boolean, e) => void, 
        classNames: string[], 
        companyVariant?: string, 
        lineItem: IJobCostLineItem 
    } & jobCostUpdater) {
	const [shouldValidate, setShouldValidate] = useState(alwaysValidate)
	const handleUpdate = useCallback((path, autoSave, e) => {
		e.preventDefault();
    
		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;
    
		updateModel((model, callback) => {
			model.setAt(path, newVal);
    
			callback(model, path);
		}, { autoSave: autoSave });
	}, [updateModel]);
    
	const validate = useCallback((property?) => {
		if (!property || shouldValidate) {
			const result = !lineItem.test(property);
    
			return result;
		}
		return true;
	}, [lineItem, shouldValidate]);
    
	const handleButtonClick = useCallback((e) => {
		e.preventDefault();
    
		const valid = validate();
    
		if (!valid) {
			setShouldValidate(false);
		}
		else {
			// this assumes that the add handler succeeds
			setShouldValidate(false);
		}
    
		buttonHandler(valid, e);
	}, [buttonHandler]);

	const globalConfig = globalRelay.config!.withVariant(companyVariant);
	const customUnits = globalConfig.jobCostSettings.installation.customUnits;
	const unitOptions = _.map(customUnits, unit => {
		return <option key={unit.value} value={unit.value}>{unit.label}</option>;
	});

	return (<tr className={classNames.join(' ')}>
		<td />
		<td className={' ' + (!validate('description') ? 'required' : '')}>
			<input type="text" placeholder="Enter" autoCapitalize="" autoCorrect="off" 
				value={lineItem.viewValue('description')} 
				onChange={e => handleUpdate('description', false, e)} 
				onBlur={e => handleUpdate('description', true, e)} 
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={(!validate('unit') ? 'required' : '')} colSpan={ 2 }>
			<select value={ lineItem.viewValue('unit') } onChange={e => handleUpdate('unit', true, e)}>
				{ unitOptions }
			</select>
		</td>
		<td className={'cost ' + ' ' + (!validate('cost') ? 'required' : '')}>
			<CurrencyInput placeholder="0.00" value={lineItem.viewValue('cost')} 
				onChange={e => handleUpdate('cost', false, e)} 
				onBlur={e => handleUpdate('cost', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className={'quantity ' + ' ' + (!validate('quantity') ? 'required' : '')}>
			<NumericInput placeholder="0" value={lineItem.viewValue('quantity')} 
				onChange={e => handleUpdate('quantity', false, e)} 
				onBlur={e => handleUpdate('quantity', true, e)}
				onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))}/>
		</td>
		<td className="cost"><span className="readOnly">{util.formatAsCurrency(lineItem.totalCost)}</span></td>
		<td><button className={buttonClass} onClick={ handleButtonClick }><span /></button></td>
		<td />
	</tr>
	);
}

const CustomSection = jobCostRowOwner({ listAccessor: model => model.lineItems, listFilterThunkFactory: props => row => row.type === props.type }, class Custom extends React.Component<any,any> {
	constructor (props) {
		super(props);

		const state: { pendingLineItem?: IJobCostLineItem } = {};

		if (!state.pendingLineItem || (state.pendingLineItem.type !== props.type || state.pendingLineItem.installSheetKey !== props.installSheetKey)) {
			state.pendingLineItem = this.createPendingLineItem(props);
		}

		this.state = state;
	}

	addLineItemHandler (lineItemKey, isValid) {
		if (!isValid) {
			return;
		}

		this.props.updateModel((model, lineUpdateCallback) => {
			if (lineItemKey === this.state.pendingLineItem.key) {
				this.state.pendingLineItem.sortIndex = this.props.getNextSortIndex(model);
				const addedItem = model.lineItems.add(this.state.pendingLineItem);
				this.props.sortRows(model);

				lineUpdateCallback(addedItem);

				this.setState({ pendingLineItem: this.createPendingLineItem(this.props) });
			}
		}, { autoSave: true });
	}

	createPendingLineItem (props) {
		const { installSheetKey, jobCost, type } = props;
		const globalConfig = globalRelay.config!.withVariant(jobCost.companyVariant);
		const customUnits = globalConfig.jobCostSettings.installation.customUnits;

		return props.jobCost.createLineItem({ type: type, margin: jobCost.margin, group: 'Custom', installSheetKey, unit: customUnits[0].value, cost: customUnits[0].defaultCost });
	}

	updateModel (key, updater, options) {
		const { pendingLineItem } = this.state;

		this.props.updateModel((jobCost, lineUpdateCallback) => {
			let lineItem = jobCost.lineItems.get(key);

			if ((!lineItem) && (key === pendingLineItem.key)) {
				lineItem = pendingLineItem;
				options.autoSave = false;
			}

			if (lineItem) {
				updater(lineItem, lineUpdateCallback);
			}
		}, options);
	}

	render () {
		const { getRowClasses, installSheetKey, jobCost, rowContextMenuHandler, type, toggleExpander, isExpanded } = this.props;

		const rows = jobCost.withStrictFilters(strictJobCost => {
			return _.map(_.sortBy(_.filter(strictJobCost.getLineItemsByType(type), { installSheetKey }), 'sortIndex'), item => {
				return (
					<CustomTableRow key={item.rowKey} updateModel={this.updateModel.bind(this, item.key)} companyVariant={ jobCost.companyVariant } buttonHandler={ rowContextMenuHandler.bind(this, item)} lineItem={item} classNames={ getRowClasses({ item: item }) } alwaysValidate={true} buttonClass="icon iconOnly contextMenu" />
				);
			});
		});

		return (
			<Expander title="CUSTOM" toggleExpander={ toggleExpander } isExpanded={isExpanded}>
				<div className="inner">
					<table className="compact prepopulated" cellPadding="0" cellSpacing="0">
						<thead>
							<tr>
								<td />
								<td><span className="readOnly">Description</span></td>
								<td className="type"><span className="readOnly">Type</span></td>
								<td className="cost" />
								<td className="cost"><span className="readOnly">Cost Per</span></td>
								<td className="quantity"><span className="readOnly">Quantity</span></td>
								<td className="cost"><span className="readOnly">Cost</span></td>
								<td className="action" />
								<td />
							</tr>
						</thead>
						<tbody>
							<JobCostHiddenWhenLocked jobCost={ jobCost }><CustomTableRow key={this.state.pendingLineItem.key} updateModel={this.updateModel.bind(this, this.state.pendingLineItem.key)} buttonHandler={this.addLineItemHandler.bind(this, this.state.pendingLineItem.key)} lineItem={this.state.pendingLineItem} classNames={ ['addRow'] } buttonClass="icon iconOnly addItem" /></JobCostHiddenWhenLocked>
							{rows}
						</tbody>
					</table>
				</div>
			</Expander>
		);
	}
});

class InstallTableRow extends React.Component<any,any> {
	private _needsSave;
	private _lastQuantityText;
	private _lastCostText;
	private updateQuantityAndSave;
	private updateCostAndSave;
	private _lastVals;

	constructor (props) {
		super(props);

		this._needsSave = false;
		this._lastQuantityText = props.item.quantity && props.item.quantity.toString();
		this._lastCostText = props.item.cost && props.item.cost.toString();
		this.handleToggle = this.handleToggle.bind(this);
		this.updateQuantityAndSave = this.updateQuantity.bind(this, true);
		this.updateQuantity = this.updateQuantity.bind(this, false);
		this.updateCostAndSave = this.updateCost.bind(this, true);
		this.updateCost = this.updateCost.bind(this, false);
		this._lastVals = { quantity: props.item.quantity, cost: props.item.cost, sortIndex: props.item.sortIndex, key: props.item.key };
	}

	shouldComponentUpdate (nextProps) {
		const result = !this._lastVals || (nextProps.item.key !== this._lastVals.key || nextProps.item.quantity !== this._lastVals.quantity || nextProps.item.cost !== this._lastVals.cost || nextProps.item.sortIndex !== this._lastVals.sortIndex);

		return result;
	}

	_updateLastVals (quantity, cost, sortIndex) {
		this._lastVals = { quantity: quantity, cost: cost, sortIndex: sortIndex };
	}

	updateQuantity (autoSave, e) {
		this._updateQuantity(e.target.value, autoSave);
	}

	handleToggle (newButton) {
		this._updateQuantity(newButton.value, true);
	}

	_updateQuantity (quantity, autoSave) {
		const { installationMargin, item, updateModel } = this.props;

		// this helps optimize performance by limiting component updates
		if (this._needsSave || this._lastQuantityText !== quantity) {
			const cleanVal = valueFilters.cleanNumber(quantity) || '0';
			const newVal = (autoSave) ? cleanVal : quantity;
			const newValNum = Number(cleanVal);

			this._lastQuantityText = quantity;

			updateModel((jobCost, lineUpdateCallback) => {
				let lineItem = _.find(jobCost.lineItems, { key: item.key });
				const isNew = !lineItem;

				if (isNew && newValNum !== 0) {
					lineItem = jobCost.lineItems.add(item);
				}

				if (autoSave && newValNum <= 0 && !isNew) {
					jobCost.lineItems.delete(lineItem.key);
					lineUpdateCallback(lineItem, '', true);
					lineItem = undefined;
				}

				if (lineItem) {
					lineItem.margin = installationMargin;
					lineItem.quantity = newVal || '0';
					lineUpdateCallback(lineItem, 'quantity');
				}

				if (autoSave) {
					this._lastVals = { quantity: lineItem && lineItem.quantity || null, cost: item.cost, sortIndex: item.sortIndex, key: item.key };
				}

				this._needsSave = !autoSave;
			}, { autoSave: autoSave, debounceTimeout: 600 });
		}
	}

	updateCost (autoSave, e) {
		const { installationMargin, item, updateModel } = this.props;
		const targetVal = e.target.value;

		// this helps optimize performance by limiting component updates
		if (this._needsSave || this._lastCostText !== targetVal) {
			const cleanVal = valueFilters.cleanNumber(e.target.value) || '0';
			const newVal = (autoSave) ? cleanVal : targetVal;
			const newValNum = Number(cleanVal);

			this._lastCostText = targetVal;

			updateModel((jobCost, lineUpdateCallback) => {
				let lineItem = _.find(jobCost.lineItems, { key: item.key });
				const isNew = !lineItem;

				if (isNew && newValNum !== 0) {
					lineItem = jobCost.lineItems.add(item);
				}

				if (autoSave && newValNum <= 0 && !isNew) {
					jobCost.lineItems.delete(lineItem.key);
					lineUpdateCallback(lineItem, '', true);
					lineItem = undefined;
				}

				if (lineItem) {
					lineItem.margin = installationMargin;
					lineItem.quantity = item.quantity;
					lineItem.cost = newVal || '0';
					lineUpdateCallback(lineItem, 'cost');
				}

				if (autoSave) {
					this._lastVals = { quantity: lineItem.quantity, cost: lineItem && lineItem.cost || null, sortIndex: item.sortIndex, key: item.key };
				}

				this._needsSave = !autoSave;
			}, { autoSave: autoSave, debounceTimeout: 600 });
		}
	}

	validate (property) {
		const { jobCost, item } = this.props;
		const lineItem = _.find(jobCost.lineItems, { key: item.key });

		return !lineItem || !lineItem.test(property);
	}

	render () {
		const { item, classNames, group } = this.props;
		const unitValue = (!item.costFactor) ? '-' : item.costFactor;
		const quantityClasses = group.renderType === 'calculated' ? ['centered'] : ['quantity'];
		let quantityColumn;
		let totalCostColumn;
		let costColumn;

		// this._updateLastVals(item.quantity, item.cost, item.sortIndex);

		switch (item.controlType) {
		case 'boolean': {
			const itemButtons = [
				{ classNames: ['icon yes'], isSelected: !!item.quantity, value: '1', key: `${item.key}-on` },
				{ classNames: ['icon no'], isSelected: !item.quantity, value: '0', key: `${item.key}-off` }
			];
			quantityColumn = <td className={ quantityClasses.join(' ') }><Radio buttons={ itemButtons } onChange={ this.handleToggle } /></td>;
			break;
		}
		case 'none':
			quantityColumn = <td className={ quantityClasses.join(' ') } />;
			break;
		default:
			if (item.editableProperty === 'cost') {
				quantityColumn = <td className={ quantityClasses.join(' ') }><span className="readOnly">–</span></td>;
			}
			else {
				quantityColumn = item.isAutoCalculated ? <td className={ quantityClasses.join(' ') }>{ (item.quantity || 0).toFixed(2) }</td> : <td className={quantityClasses.join(' ') + ' ' + (!this.validate('quantity') ? 'required' : '')}><NumericInput placeholder="0" value={ util.reactVal(item.quantity)} onChange={this.updateQuantity} onBlur={this.updateQuantityAndSave} preventSelectOnFocus={ true } /></td>;
			}
		}

		if (item.editableProperty === 'cost') {
			costColumn = <td className="cost"><span className="readOnly">-</span></td>;
			totalCostColumn = <td className="cost"><CurrencyInput placeholder="0.00" value={ item.cost || '' } onChange={ this.updateCost } onBlur={ this.updateCostAndSave }/></td>;
		}
		else {
			costColumn = group.renderType === 'calculated' ? null : <td className="cost"><span className="readOnly">{ util.formatAsCurrency(item.cost) }</span></td>;
			totalCostColumn = <td className="cost"><span className="readOnly">{ util.formatAsCurrency(item.cost * item.quantity) }</span></td>;
		}

		// NOTE: item is not a line item model, so need to calculate totalCost using cost * quantity
		return (
			<tr className={ classNames.join(' ') }>
				<td />
				<td><span className="readOnly">{ item.description}</span></td>
				{ group.renderType === 'calculated' ? null : <td><span className="readOnly">{ item.unit || '-' }</span></td> }
				{ group.renderType === 'calculated' ? null : <td className="cost"><span className="readOnly">{ unitValue }</span></td> }
				{ costColumn }
				{ quantityColumn }
				{ totalCostColumn }
				<td><TooltipTarget text={ item.helpText }><button tabIndex={-1} className="icon iconOnly info"><span /></button></TooltipTarget></td>
				<td />
			</tr>
		);
	}
}

const CalculationHeaderRow = () => {
	return (<tr>
		<td />
		<td><span className="readOnly">Description</span></td>
		<td className="centered"><span className="readOnly">Options</span></td>
		<td className="cost"><span className="readOnly">Cost</span></td>
		<td className="action" />
		<td />
	</tr>);
};

const StandardHeaderRow = () => {
	return (<tr>
		<td />
		<td><span className="readOnly">Description</span></td>
		<td className="type"><span className="readOnly">Type</span></td>
		<td className="cost"><span className="readOnly">Unit Value</span></td>
		<td className="cost"><span className="readOnly">Cost Per</span></td>
		<td className="quantity"><span className="readOnly">Quantity</span></td>
		<td className="cost"><span className="readOnly">Cost</span></td>
		<td className="action" />
		<td />
	</tr>);
};

const InstallationSection = jobCostRowOwner({ listAccessor: model => model.lineItems, listFilterThunkFactory: props => row => row.type === props.type }, class Section extends React.Component<any,any> {
	render () {
		const { getRowClasses, group, installationMargin, 
			lineItems, updateModel, jobCost, toggleExpander, isExpanded } = this.props;
		const tableRows = jobCost.withStrictFilters(strictJobCost => {
			return lineItems.map(item => {
				let renderItem = item;
				const lineItem = _.find(strictJobCost.lineItems, { type: item.type, group: item.group, description: item.description, installSheetKey: item.installSheetKey });
				const baseClasses = ['added'];

				if (lineItem) {
					renderItem = _.assign({}, item, { key: lineItem.key, cost: lineItem.cost, quantity: lineItem.quantity, installSheetKey: lineItem.installSheetKey });
					baseClasses.push('populated');
				}

				return (<InstallTableRow
					classNames={getRowClasses({ item: item, baseClassNames: baseClasses })}
					installationMargin={installationMargin}
					key={ renderItem.key }
					jobCost={ jobCost }
					group={ group }
					item={ renderItem }
					updateModel={ updateModel } />);
			});
		});

		const headRow = group.renderType === 'calculated' ? <CalculationHeaderRow /> : <StandardHeaderRow />;

		return (
			<Expander title={ group.name.toUpperCase() } toggleExpander={ toggleExpander } isExpanded ={isExpanded}>
				<div className="inner">
					<table className="compact prepopulated" cellPadding="0" cellSpacing="0">
						<thead>
							{ headRow }
						</thead>
						<tbody>
							{tableRows}
						</tbody>
					</table>
				</div>
			</Expander>
		);
	}
});

export interface IInstallSheetRef {
    collapseAll(): void;
    configItems: Record<string, any>[];
}

export default forwardRef(function InstallSheetEdit({ jobCost, installSheet, updateModel }: IJobCostChildProps & { installSheet: IInstallSheet }, ref: Ref<IInstallSheetRef>) {
	const [expanderStates, setExpanders] = useState(() => { return {} });
	const [configItems, setConfigItems] = useState(() => [] );
	const [groups, setGroups] = useState(() => [] );
	const installSheetKey = installSheet.key;
	const {installationConfig} = actions.getInstallationConfig(jobCost);

	useImperativeHandle<IInstallSheetRef, IInstallSheetRef>(ref, () => {
		return {
			collapseAll() {
				const expanders = {...expanderStates};
    
				for(const key in expanders) {
					expanders[key] = false;
				}
                
				setExpanders(expanders);
			},
			configItems
		}
	}, [configItems, expanderStates]);

	useEffect(() => {
		const installationUnitPrice = installSheet.unitCost || jobCost.installationUnitPrice || 0;
		const configItems = [];
		const expanders: Record<string, boolean> = {};
		let configGroups = [];

		if(!installationConfig) {
			return;
		}

		// installationConfig might be a simple array or it contains an array of groups
		configGroups = _.map(installationConfig.groups, g => _.assign({ items: []}, g)) || [];

		let sortIndex = 0;
		// installationConfig might be a simple array or it contains an array of items
		_.forEach(installationConfig.items || installationConfig, configItem => {
			let group = _.find(configGroups, { name: configItem.group });
			if (!group && configItem.type !== 'service') {
				group = { name: configItem.group, items: []};
				configGroups.push(group);
			}

			const isFixedCost = typeof configItem.cost !== 'undefined';
			const filledItem = _.assign({ key: util.generateGuid(), cost: isFixedCost ? Number(configItem.cost) : Number(configItem.costFactor) * installationUnitPrice, installSheetKey, quantity: 0, sortIndex: sortIndex++ }, configItem);

			configItems.push(filledItem);
			if (group) {
				group.items.push(filledItem);
			}
		});

		if (!_.find(configGroups, { name: 'Service Items' })) {
			configGroups.push({ name: 'Service Items', items: _.filter(configItems, { type: 'service' }) });
		}

		// clear out any line items that are not valid based on the current config
		if (jobCost.allowedContractEdits !== 'none' && !jobCost.currentChangeOrder) {
			const toRemove = _.filter(jobCost.getLineItemsByType(['installation', 'service']), item => {
				return item.quantity === 0 || !_.find(configItems, { type: item.type, group: item.group, description: item.description });
			});

			if (toRemove.length && updateModel) {
				updateModel(model => {
					_.forEach(toRemove, item => {
						model.lineItems.delete(item.key);
					});
				}, { autoSave: true });
			}
		}

		for(const group of configGroups) {
			expanders[`expander-${group.name}`] = true;
		}

		const hasCustom = configGroups.find(e => e.renderType === 'custom');

		if (!hasCustom) {
			expanders['expander-custom'] = true;
		}

		setConfigItems(configItems);
		setGroups(configGroups);
		setExpanders(expanders)

	}, [installSheetKey, installationConfig]);

	const toggleExpander = useCallback((key) =>{
		const isExpanded = expanderStates[key];

		setExpanders({ ...expanderStates, ...{[key]: !isExpanded}});
	}, [expanderStates]);

	const sections:JSX.Element[] = [];
	if (groups !== undefined) {
		const installationMargin = installSheet.margin || jobCost.margin;
		let hasCustom = false;

		_.forEach(groups, group => {
			if (group.renderType === 'custom') {
				// only allow one custom section - in this case it is configured by installation config
				if (!hasCustom) {
					const isExpanded = expanderStates[`expander-${group.name}`];
					hasCustom = true;
					sections.push(<CustomSection key={ group.name } 
						jobCost={jobCost} installSheetKey={ installSheetKey } 
						updateModel={updateModel} type="installation-custom"
						toggleExpander={e => toggleExpander(`expander-${group.name}`)} 
						isExpanded={isExpanded}/>);
				}
			}
			else if (group.items.length) {
				const isExpanded = expanderStates[`expander-${group.name}`];
				sections.push(<InstallationSection key={ group.name }
					installationMargin={ installationMargin }
					installSheetKey={ installSheetKey }
					lineItems={ group.items }
					group={ group }
					jobCost = { jobCost }
					toggleExpander={e => toggleExpander(`expander-${group.name}`)}
					updateModel={ updateModel }
					isExpanded={isExpanded} />);
			}
		});

		// only allow one custom section - in this case it is not configured by installation config and defaults to the end
		if (!hasCustom) {
			const isExpanded = expanderStates['expander-custom'];
			sections.push(<CustomSection 
				key="custom" jobCost={jobCost} 
				installSheetKey={ installSheetKey } updateModel={updateModel} 
				type="installation-custom"
				toggleExpander={e => toggleExpander('expander-custom')}
				isExpanded={isExpanded}/>);
		}
	}

	return <React.Fragment>{ sections }</React.Fragment>
});